import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Tabs,
  Tab,
  styled,
  Paper,
  Grid,
  Chip,
  CircularProgress
} from '@mui/material';
import { X as CloseIcon } from 'lucide-react';
import { ShippixShipment } from '../../types/shippix';
import { shippixService } from '../../services/shippixService';
import { useAuth } from '../../contexts/AuthContext';
import PriceDisplay from '../PriceDisplay';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    width: '100%',
    maxWidth: '1000px',
    margin: '16px',
    borderRadius: '12px',
    overflow: 'hidden'
  }
}));

const DialogHeader = styled(DialogTitle)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '20px 24px',
  background: '#1F2937',
  color: '#ffffff',
  '& .MuiIconButton-root': {
    color: '#ffffff',
    marginRight: '-12px',
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.1)'
    }
  }
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  borderBottom: '1px solid #E5E7EB',
  background: '#F9FAFB',
  '& .MuiTabs-indicator': {
    backgroundColor: '#2563EB',
    height: '3px'
  },
  '& .MuiTab-root': {
    textTransform: 'none',
    minWidth: 100,
    padding: '16px 24px',
    fontWeight: 500,
    fontSize: '0.875rem',
    color: '#6B7280',
    '&.Mui-selected': {
      color: '#2563EB',
      fontWeight: 600
    },
    '&:hover': {
      color: '#2563EB',
      opacity: 0.8
    }
  },
}));

const InfoCard = styled(Paper)(({ theme }) => ({
  padding: '24px',
  borderRadius: '12px',
  border: '1px solid #E5E7EB',
  boxShadow: 'none',
  height: '100%',
  backgroundColor: '#FFFFFF'
}));

const InfoSection = styled('div')({
  '& + &': {
    marginTop: '32px'
  }
});

const InfoTitle = styled(Box)({
  fontSize: '1rem',
  fontWeight: 600,
  color: '#111827',
  marginBottom: '24px',
  textTransform: 'uppercase',
  letterSpacing: '0.025em'
});

const InfoRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  marginBottom: '12px',
  '&:last-child': {
    marginBottom: 0
  }
});

const InfoLabel = styled(Box)({
  width: '120px',
  color: '#6B7280',
  fontWeight: 500,
  fontSize: '0.875rem'
});

const InfoValue = styled(Box)({
  flex: 1,
  color: '#374151',
  fontSize: '0.875rem'
});

const PriceChip = styled(Chip)({
  backgroundColor: '#DCFCE7',
  color: '#166534',
  fontWeight: 600,
  fontSize: '1rem',
  height: '32px',
  borderRadius: '8px'
});

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`shipment-tabpanel-${index}`}
      aria-labelledby={`shipment-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box>{children}</Box>
      )}
    </div>
  );
}

interface Props {
  open: boolean;
  onClose: () => void;
  shipment: ShippixShipment | null;
}

interface TrackingHistory {
  date: string;
  status: string;
  status_id: number;
  incidence: string | null;
  incidence_id: number | null;
  receiver: string | null;
  signature: string | null;
  tracking_code: string | null;
  obs: string;
}

const statusColors: Record<number, { bg: string; color: string }> = {
  1: { bg: '#FEF3C7', color: '#92400E' }, // Pendente
  2: { bg: '#E0F2FE', color: '#075985' }, // Aceite
  3: { bg: '#F3E8FF', color: '#6B21A8' }, // Em Recolha
  4: { bg: '#DBEAFE', color: '#1E40AF' }, // Em Trânsito
  5: { bg: '#E0E7FF', color: '#3730A3' }, // Em Distribuição
  6: { bg: '#DCFCE7', color: '#166534' }, // Entregue
  7: { bg: '#FEE2E2', color: '#991B1B' }, // Cancelado
  8: { bg: '#FEF3F2', color: '#9F1239' }, // Devolvido
  9: { bg: '#FEF2F2', color: '#991B1B' }  // Exceção
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('pt-PT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatCurrency = (value: string | number) => {
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  return numValue.toLocaleString('pt-PT', {
    style: 'currency',
    currency: 'EUR'
  });
};

const calculatePrice = (basePrice: string, zipCode: string) => {
  const numPrice = parseFloat(basePrice);
  
  // Verifica se é código postal dos Açores ou Madeira (começa com 9)
  // ou se não está no formato XXXX-XXX
  if (zipCode.startsWith('9') || !/^\d{4}-\d{3}$/.test(zipCode)) {
    return formatCurrency(numPrice);
  }

  // Adiciona 23% de IVA
  const priceWithVAT = numPrice * 1.23;
  return `${formatCurrency(numPrice)} + IVA (${formatCurrency(priceWithVAT)})`;
};

export default function ShipmentDetailsModal({ open, onClose, shipment }: Props) {
  const { currentUser } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [trackingHistory, setTrackingHistory] = useState<TrackingHistory[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState('');

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    if (tabValue === 1 && shipment && currentUser?.companyId) {
      setLoadingHistory(true);
      setHistoryError('');
      
      shippixService.getShipmentHistory(currentUser.companyId, shipment.tracking_code)
        .then(history => {
          setTrackingHistory(history);
        })
        .catch(error => {
          console.error('Erro ao carregar histórico:', error);
          setHistoryError('Não foi possível carregar o histórico do envio.');
        })
        .finally(() => {
          setLoadingHistory(false);
        });
    }
  }, [tabValue, shipment, currentUser?.companyId]);

  if (!shipment) return null;

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      aria-labelledby="shipment-details-title"
      maxWidth={false}
    >
      <DialogHeader id="shipment-details-title">
        <Box>
          <Box variant="h6" component="h2" sx={{ mb: 0.5 }}>
            Serviço #{shipment.tracking_code}
          </Box>
          <Box variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
            {shipment.service.name} • {formatDate(shipment.date)}
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogHeader>

      <StyledTabs value={tabValue} onChange={handleTabChange}>
        <Tab label="Detalhes" />
        <Tab label="Tracking" />
      </StyledTabs>

      <DialogContent sx={{ p: 3 }}>
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <InfoCard>
                <InfoSection>
                  <InfoTitle>Informações do Envio</InfoTitle>
                  <InfoRow>
                    <InfoLabel>Tracking</InfoLabel>
                    <InfoValue>{shipment.tracking_code}</InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>Estado</InfoLabel>
                    <InfoValue>
                      <Box component="span">
                        <Chip
                          label={shipment.status.name}
                          sx={{
                            backgroundColor: statusColors[shipment.status.id]?.bg || '#F3F4F6',
                            color: statusColors[shipment.status.id]?.color || '#374151'
                          }}
                        />
                      </Box>
                    </InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>Data de Criação</InfoLabel>
                    <InfoValue>{formatDate(shipment.date)}</InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>Última Atualização</InfoLabel>
                    <InfoValue>{formatDate(shipment.status_date)}</InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>Operador</InfoLabel>
                    <InfoValue>{shipment.service.name}</InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>Serviço</InfoLabel>
                    <InfoValue>{shipment.service.name}</InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>Valor</InfoLabel>
                    <InfoValue>
                      <PriceDisplay 
                        basePrice={shipment.total_price}
                        zipCode={shipment.recipient_zip_code}
                        serviceCode={shipment.service.code}
                      />
                    </InfoValue>
                  </InfoRow>
                </InfoSection>
              </InfoCard>
            </Grid>

            <Grid item xs={12} md={6}>
              <InfoCard>
                <InfoSection>
                  <InfoTitle>Destinatário</InfoTitle>
                  <InfoRow>
                    <InfoLabel>Nome</InfoLabel>
                    <InfoValue>{shipment.recipient_name}</InfoValue>
                  </InfoRow>
                  {shipment.recipient_attn && (
                    <InfoRow>
                      <InfoLabel>Contacto</InfoLabel>
                      <InfoValue>{shipment.recipient_attn}</InfoValue>
                    </InfoRow>
                  )}
                  <InfoRow>
                    <InfoLabel>Morada</InfoLabel>
                    <InfoValue>{shipment.recipient_address}</InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>Código Postal</InfoLabel>
                    <InfoValue>{shipment.recipient_zip_code}</InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>Cidade</InfoLabel>
                    <InfoValue>{shipment.recipient_city}</InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>País</InfoLabel>
                    <InfoValue>{shipment.recipient_country.toUpperCase()}</InfoValue>
                  </InfoRow>
                  <InfoRow>
                    <InfoLabel>Telefone</InfoLabel>
                    <InfoValue>{shipment.recipient_phone || '-'}</InfoValue>
                  </InfoRow>
                  {shipment.recipient_email && (
                    <InfoRow>
                      <InfoLabel>Email</InfoLabel>
                      <InfoValue>{shipment.recipient_email}</InfoValue>
                    </InfoRow>
                  )}
                  {shipment.recipient_vat && (
                    <InfoRow>
                      <InfoLabel>NIF</InfoLabel>
                      <InfoValue>{shipment.recipient_vat}</InfoValue>
                    </InfoRow>
                  )}
                </InfoSection>
              </InfoCard>
            </Grid>

            <Grid item xs={12}>
              <InfoCard>
                <InfoSection>
                  <InfoTitle>Detalhes do Pacote</InfoTitle>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <InfoRow>
                        <InfoLabel>Volumes</InfoLabel>
                        <InfoValue>{shipment.volumes}</InfoValue>
                      </InfoRow>
                      <InfoRow>
                        <InfoLabel>Peso</InfoLabel>
                        <InfoValue>{shipment.weight} kg</InfoValue>
                      </InfoRow>
                      <InfoRow>
                        <InfoLabel>Peso Volumétrico</InfoLabel>
                        <InfoValue>{shipment.volumetric_weight} kg</InfoValue>
                      </InfoRow>
                      {(shipment.obs || shipment.obs_delivery) && (
                        <>
                          <InfoRow>
                            <InfoLabel>Observações</InfoLabel>
                            <InfoValue>{shipment.obs || shipment.obs_delivery}</InfoValue>
                          </InfoRow>
                        </>
                      )}
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <InfoRow>
                        <InfoLabel>Referência</InfoLabel>
                        <InfoValue>{shipment.reference || '-'}</InfoValue>
                      </InfoRow>
                    </Grid>
                  </Grid>
                </InfoSection>
              </InfoCard>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <InfoCard>
            {loadingHistory ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            ) : historyError ? (
              <Box variant="body2" color="error" align="center" p={4}>
                {historyError}
              </Box>
            ) : (
              <Box>
                {trackingHistory.map((event, index) => (
                  <Box
                    key={index}
                    sx={{
                      position: 'relative',
                      pb: index === trackingHistory.length - 1 ? 0 : 4,
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: '11px',
                        top: '24px',
                        bottom: 0,
                        width: '2px',
                        bgcolor: index === trackingHistory.length - 1 ? 'transparent' : '#E5E7EB'
                      }
                    }}
                  >
                    <Box display="flex" gap={2}>
                      <Box
                        sx={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '50%',
                          bgcolor: statusColors[event.status_id]?.bg || '#F3F4F6',
                          border: '2px solid',
                          borderColor: statusColors[event.status_id]?.color || '#374151',
                          flexShrink: 0
                        }}
                      />
                      <Box>
                        <Box variant="subtitle2" sx={{ color: '#111827', mb: 0.5 }}>
                          {event.status}
                          {event.incidence && ` - ${event.incidence}`}
                        </Box>
                        <Box variant="body2" sx={{ color: '#6B7280', mb: 0.5 }}>
                          {formatDate(event.date)}
                        </Box>
                        {event.receiver && (
                          <Box variant="body2" sx={{ color: '#374151' }}>
                            Recebido por: {event.receiver}
                          </Box>
                        )}
                        {event.obs && (
                          <Box 
                            variant="body2" 
                            sx={{ 
                              color: '#374151', 
                              mt: 0.5,
                              '& a': {
                                color: '#2563EB',
                                textDecoration: 'none',
                                '&:hover': {
                                  textDecoration: 'underline'
                                }
                              }
                            }}
                            dangerouslySetInnerHTML={{ __html: event.obs }}
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </InfoCard>
        </TabPanel>
      </DialogContent>
    </StyledDialog>
  );
}
